// Retrospective ("aha") follow-up regime, selected by an intentionally non-obvious
// URL param so it can be exercised on the live site without tipping off participants.
//
// The param LOOKS like a build/version tag (`?s=v9`) and its value is NOT the raw N —
// it's a codebook entry, so poking at numbers doesn't reveal a clean N→behaviour
// gradient. Unknown/absent codes fall back to the production default (never to a
// disabled state). The resolved N *and* the raw code are recorded on every session
// (see saveConsent) + every retro doc, so any tampering is detectable and filterable.
//
// N semantics: the retrospective question is appended after any SOLVED test-mode puzzle
// whose 0-based trialIndex >= N. So N=9 → first retro after the 10th puzzle; N=0 →
// after every puzzle (Phase-2 after-each); Infinity → never asked.
//
// To switch the whole cohort at Phase 2, change DEFAULT_RETRO_AFTER and redeploy — the
// no-param default moves everyone together (avoids an accidental two-links-two-regimes
// sample split). The codebook is for testing specific regimes on demand.

const DEFAULT_RETRO_AFTER = 9

// Private codebook. Values are deliberately non-sequential so the code ≠ N.
const CODEBOOK: Record<string, number> = {
  v9: 9, // production baseline (retro after the 10th puzzle only, then per extension puzzle)
  v4: 1, // quick test: retro from the 2nd puzzle onward
  v7: 0, // after-each from the first puzzle (Phase-2 regime)
  v2: Infinity, // off: never ask (pure Wurgaft-comparable baseline)
}

export type RetroConfig = {
  n: number // threshold; retro fires on solved test puzzles with trialIndex >= n
  code: string | null // raw ?s= value seen (for provenance), or null if absent
}

export function getRetroAfter(): RetroConfig {
  try {
    const raw = new URLSearchParams(window.location.search).get('s')
    if (raw && Object.prototype.hasOwnProperty.call(CODEBOOK, raw)) {
      return { n: CODEBOOK[raw], code: raw }
    }
    return { n: DEFAULT_RETRO_AFTER, code: raw }
  } catch {
    return { n: DEFAULT_RETRO_AFTER, code: null }
  }
}
